import { Dropdown } from "primereact/dropdown"
import { Panel } from "primereact/panel"
import { useState, useEffect, useMemo } from "react"
import { Product_Output, ProductionTask_Input } from "@/api"
import { formatFloat } from "@/utils/format"
import { D } from "node_modules/@tanstack/query-core/build/modern/hydration-B_mC2U5v"
import { SelectItemOptionsType } from "primereact/selectitem"

interface DateSelectorProps {
  dates?: string[]
  selectedDate?: string | null
  onDateChange?: (date: string | null) => void
  holidayDates?: string[] // 一斉休業日のリスト
  productionTasks?: ProductionTask_Input[]
  productsById?: Record<string, Product_Output>
}

const DateSelector = ({
  dates = [],
  selectedDate,
  onDateChange,
  holidayDates,
  productionTasks = [],
  productsById = {},
}: DateSelectorProps) => {
  const [internalSelectedDate, setInternalSelectedDate] = useState<string | null>(
    selectedDate || null
  )

  // 外部からのselectedDateの変更を内部状態に反映
  useEffect(() => {
    setInternalSelectedDate(selectedDate || null)
  }, [selectedDate])

  const totalDurationByDate = useMemo(() => {
    const totals = new Map<string, number>()
    if (!productionTasks.length) return totals
    productionTasks.forEach((task) => {
      const duration = task.duration ?? 0
      totals.set(task.date, (totals.get(task.date) ?? 0) + duration)
    })
    return totals
  }, [productionTasks, productsById])

  // 日付のオプションを作成
  const dateOptions = dates.map((date) => {
    const isHoliday = holidayDates?.includes(date) || false
    const totalSeconds = totalDurationByDate.get(date) ?? 0
    const totalHoursLabel = totalSeconds > 0 ? formatFloat(totalSeconds / 3600, 1) : "0"
    const baseLabel = isHoliday ? `${date} (休業日)` : date
    let className = isHoliday && totalSeconds == 0 ? "holiday-date-option" : "date-heat-option"
    if(totalSeconds >= 100 * 3600) {
      className += " high"
    } else if(totalSeconds >= 50 * 3600) {
      className += " medium"
    } else if(totalSeconds > 0) {
      className += " low"
    }

    return {
      label: `${baseLabel} - ${totalHoursLabel}h`,
      value: date,
      className
    }
  })

  const handleDateChange = (value: string | null) => {
    setInternalSelectedDate(value)
    if (onDateChange) {
      onDateChange(value)
    }
  }

  return (
    <Panel toggleable header="日付毎のサマリ" className="w-full">
    <div className="p-3">
      <div className="mb-2">
        <label className="font-semibold text-sm">日付選択</label>
      </div>
      <Dropdown
        value={internalSelectedDate}
        onChange={(e) => handleDateChange(e.value)}
        options={dateOptions}
        placeholder="日付を選択してください"
        className="w-full"
        showClear
      />
      {dates.length > 0 && (
        <div className="mt-2 text-xs text-600">
          期間: {dates[0]} ～ {dates[dates.length - 1]}
        </div>
      )}
    </div>
    </Panel>
  )
}

export default DateSelector
